import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';

export interface SubscriptionState {
  status: string | null;
  priceId: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  hasActiveSub: boolean;
  hasLifetimePro: boolean;
  isTrialing: boolean;
  hasAccess: boolean;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Read-only account access hook: paid Stripe status + time-limited trial +
 * independent, owner-issued, permanent free Pro entitlement.
 * Database authorization also checks public.pro_access_grants through
 * private.has_premium_access; browser display is NOT the access authority.
 */
export function useSubscription(): SubscriptionState {
  const { user } = useAuth();
  const { isAdmin, isLoading: roleLoading } = useUserRole();

  const { data, isLoading: subLoading, refetch } = useQuery({
    queryKey: ['subscription', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('status, price_id, current_period_end, cancel_at_period_end, trial_ends_at')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: permanentGrant, isLoading: grantLoading, refetch: refetchGrant } = useQuery({
    queryKey: ['permanent-pro-grant', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data: grant, error } = await supabase
        .from('pro_access_grants')
        .select('user_id')
        .eq('user_id', user!.id)
        .maybeSingle();
      // Tabelle existiert in Production und ab S1b E0 auf Staging (Lifetime = Pro). Nur als Fehlerbehandlung:
      // fehlt sie trotzdem (altes Schema), zählt das als „kein Grant“ statt die Seite in Fehler laufen zu lassen.
      if (error && (error.code === 'PGRST205' || error.code === '42P01')) {
        console.warn('[useSubscription] pro_access_grants fehlt im Schema – Lifetime-Pro nicht prüfbar');
        return null;
      }
      if (error) throw error;
      return grant;
    },
  });

  const status = (data?.status as string | null) ?? null;
  const priceId = (data?.price_id as string | null) ?? null;
  const currentPeriodEnd = (data?.current_period_end as string | null) ?? null;
  const cancelAtPeriodEnd = Boolean(data?.cancel_at_period_end);
  const trialEndsAt = (data?.trial_ends_at as string | null) ?? null;

  const hasLifetimePro = !!permanentGrant;
  const hasActiveSub = status === 'active' || status === 'trialing' || isAdmin;
  const isTrialing = !!trialEndsAt && new Date(trialEndsAt).getTime() > Date.now();
  const hasAccess = hasActiveSub || isTrialing || hasLifetimePro;
  const isLoading = subLoading || grantLoading || (!!user && roleLoading);
  const refetchAll = useCallback(() => {
    void refetch();
    void refetchGrant();
  }, [refetch, refetchGrant]);

  return {
    status,
    priceId,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    trialEndsAt,
    hasActiveSub,
    hasLifetimePro,
    isTrialing,
    hasAccess,
    isLoading,
    refetch: refetchAll,
  };
}
